import React, { useContext, useRef } from 'react';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import TagsLists from 'components/Dashboard/TagsList';
import { store } from 'components/Store';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemContent,
  ListItemHeader,
  ListItemLink,
} from 'components/Dashboard/ItemController/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import time from 'utils/time';
import { flattenListRowProps } from 'components/Dashboard/ListControl/listRowProps';
import preventLink from 'components/Dashboard/TagsList/prevenLink';

const LibraryMaterialItem = ({ listRow }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;
  const { list, activeSearch } = search;

  const { listContext = {}, feedOverrides = {}, display = {}, interactions = {} } = listRow ?? {};
  const { item, scrollBarRef, index } = listContext;
  const { feedItemFlags } = feedOverrides;
  const { hideYellowDot, showContentTypeBadge, notShowShare, tagsFixedPosition, onlyEmbargoedTag, tooltipText } =
    display;
  const { itemOnclick, thumbDownProps, itemActionsProps, showPriorityBadge, setActiveUpdate } = interactions;
  const tagsListsProps = flattenListRowProps(listRow);
  const { contentUrl, title, dateTime, articleTypes, house } = item;

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });
  const { rememberPosition } = useRememberPosition();

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useListItemController({
    item,
    index,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
    feedItemFlags,
  });

  return (
    <ListItemLink
      to={locationDescriptor(contentUrl)}
      onClick={(e) => {
        if (rateUpdatesMode && setActiveUpdate) {
          preventLink(e);
          setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
          return;
        }

        if (scrollBarRef) {
          rememberPosition('LibraryMaterial', scrollBarRef, index);
        }
        if (itemOnclick) {
          itemOnclick(e);
        }
      }}
      tooltipText={tooltipText}
    >
      <ListItemContainer className='my-2 content-item' unseen={unseenConditional}>
        <ListItemContent>
          <ListItemHeader
            className='justify-content-between mb-2'
            mainContent={
              showContentTypeBadge ? (
                <div className='mt-1 specific-bottom-margin-4 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                  <ContentTypeBadge objectType={item.type} name={'Library material'} />
                </div>
              ) : (
                <h4 className={`mb-0 flex-grow-1 content-item-title`}>{title}</h4>
              )
            }
            actions={
              !notShowShare ? (
                <ItemControllerActions
                  rateUpdatesMode={rateUpdatesMode}
                  ratingFeedback={feedItemFlags?.[index]?.importanceRating}
                  priorityBadge={
                    priorityBadgeCondition ? <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} /> : null
                  }
                  actions={
                    <GeneralItemActions
                      item={item}
                      index={index}
                      thumbDownProps={thumbDownProps}
                      itemActionsProps={itemActionsProps}
                    />
                  }
                />
              ) : null
            }
          />
          {showContentTypeBadge && <h4 className={`mb-2 flex-grow-1 content-item-title`}>{title}</h4>}
          <div className={`content-title w-100`}>
            <p className='heading-date mb-0'>
              House of {house ?? 'Commons'} Library
              {articleTypes?.length > 0 && (
                <>
                  <span className='dot' />
                  {articleTypes.join(', ')}
                </>
              )}
              <span className='dot' />
              <span className='date'>{time(dateTime).formatLongDate()}</span>
            </p>
            <p
              className='title-body my-2 position-relative'
              ref={titleBodyComponent}
              dangerouslySetInnerHTML={createMarkup(bodySnippet)}
            />
          </div>

          {list !== null && activeSearch && (
            <TagsLists
              {...tagsListsProps}
              feedItemFlags={feedItemFlags}
              index={index}
              section={'LibraryMaterial'}
              tagsFixedPosition={tagsFixedPosition}
              scrollBarRef={scrollBarRef}
              onlyEmbargoedTag={onlyEmbargoedTag}
            />
          )}
        </ListItemContent>
      </ListItemContainer>
    </ListItemLink>
  );
};
export default LibraryMaterialItem;
